import { useRef, useState } from 'react';
import { formatDayMonthYear, maskDayMonthYear, parseDayMonthYear } from '@airpost/shared';
import { t } from './i18n';

interface Props {
  /** ISO `YYYY-MM-DD`, or '' for empty. */
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * A date box that always reads day/month/year (26/10/2026), whatever the browser's locale: a text field
 * with a typing mask, plus a calendar button that opens the browser's own picker. The value in and out
 * is ISO, as the API stores it.
 */
export function DateInput({ value, onChange, id, required, disabled, className }: Props) {
  const [state, setState] = useState({ forValue: value, text: formatDayMonthYear(value) });
  const [invalid, setInvalid] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  // The outside value moved (reset button, dialog reopened, picker used): show it.
  if (state.forValue !== value) {
    setState({ forValue: value, text: formatDayMonthYear(value) });
    setInvalid(false);
  }

  const typed = (raw: string) => {
    const text = maskDayMonthYear(raw);
    setState({ forValue: value, text });
    if (text === '') {
      setInvalid(false);
      if (value) onChange('');
      return;
    }
    const iso = parseDayMonthYear(text);
    if (iso) {
      setInvalid(false);
      if (iso !== value) onChange(iso);
    } else {
      setInvalid(text.length === 10);
    }
  };

  const blurred = () => {
    // Half-typed or impossible dates do not survive leaving the box: back to the last real value.
    if (state.text !== '' && parseDayMonthYear(state.text) === null) {
      setState({ forValue: value, text: formatDayMonthYear(value) });
      setInvalid(false);
    }
  };

  const openPicker = () => {
    const el = picker.current;
    if (!el || disabled) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
      el.click();
    }
  };

  return (
    <span className={className ? `date-field ${className}` : 'date-field'}>
      <input
        id={id}
        className="date-text"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t('datePlaceholder')}
        title={invalid ? t('badDate') : t('datePlaceholder')}
        aria-invalid={invalid || undefined}
        maxLength={10}
        required={required}
        disabled={disabled}
        value={state.text}
        onChange={(e) => typed(e.target.value)}
        onBlur={blurred}
      />
      <button type="button" className="date-pick" aria-label={t('pickDate')} title={t('pickDate')} disabled={disabled} onClick={openPicker}>
        <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.4" fill="none" />
          <rect x="4" y="8" width="2.2" height="2.2" fill="currentColor" />
          <rect x="7" y="8" width="2.2" height="2.2" fill="currentColor" />
          <rect x="10" y="8" width="2.2" height="2.2" fill="currentColor" />
        </svg>
      </button>
      <input ref={picker} className="date-native" type="date" tabIndex={-1} aria-hidden="true" disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}
